<template>
  <!--  the bar-->
  <!--  Add padding to div below-->
  <top-navigation
    previous-route="Quotes"
    :title="$t('components.comment.title')"
  />
  <div class="mx-auto min-h-screen pb-12 pt-24">
    <div
      v-if="quoteDoc"
      class="sticky top-24 z-20 mb-8 w-full origin-top transition-all duration-500"
    >
      <quote :quote-id="quoteId" :quote-data="quoteDoc" />
    </div>

    <InformationText
      v-if="fetchError"
      color="red"
      class="mx-2 mb-2"
      :information="fetchError"
    />

    <SkeletonLoader v-if="loading"></SkeletonLoader>

    <div v-if="quoteDoc">
      <comment
        v-for="(comment, index) in comments"
        :key="`${comment.author}-${index}`"
        :author-uuid="comment.author"
        :previous-date="getPreviousDate(index)"
        :date="comment.created_at"
        :comment="comment.comment"
      />
    </div>
  </div>
  <div class="fixed bottom-0 left-0 z-20 flex w-full justify-center">
    <div class="w-full max-w-xl bg-white px-2 pb-4 dark:bg-kekkle-dark">
      <comment-input
        :posting-comment="posting"
        @post-comment="postNewComment"
      ></comment-input>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, watch } from "vue";
import topNavigation from "../components/TopNavigation.vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import Quote from "@/components/Quote.vue";
import CommentInput from "@/components/CommentInput.vue";
import Comment from "@/components/Comment.vue";
import InformationText from "@/components/InformationText.vue";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { useComments } from "@/composables/useComments";
import { quoteCommentDocumentConverter } from "@kekkle/shared/convertors/web/comment-document.converter";
import router from "@/router/router";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import SkeletonLoader from "../components/ui/SkeletonLoader.vue";

type Props = {
  quoteId: string;
  groupId: string;
};

const props = defineProps<Props>();

const authStore = useAuthStore();
const userStore = useUserStore();
const groupsStore = useGroupsStore();
const quotesStore = useQuotesStore();

// Get the quote from the store's allQuotes (populated by onSnapshot listener)
const quoteDoc = computed(
  () =>
    quotesStore.allQuotes.find((q: QuoteDocument) => q._id === props.quoteId) ??
    null,
);

const {
  comments,
  posting,
  fetchError,
  loading,
  postComment: postCommentToFirestore,
} = useComments({
  groupId: props.groupId,
  itemId: props.quoteId,
  collectionName: "quotes_comments",
  converter: quoteCommentDocumentConverter,
  incrementField: "number_of_comments",
  stampUpdatedAt: true,
  authorUuid: authStore.authUuid ?? "",
});

boot();

watch(
  () => comments.value.length,
  async () => {
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        left: 0,
        top: document.body.scrollHeight,
      });
    }, 40);
  },
  { immediate: true },
);

async function boot() {
  if (userStore.userData?.["groups"]?.includes(props.groupId)) {
    if (userStore.activeGroup !== props.groupId) {
      if (!authStore.authUuid) {
        await router.push({ name: "Group" });
        return;
      }
      await userStore.switchActiveGroup(props.groupId);
      await groupsStore.refreshAllUserGroupsData();
    }
    // Ensure quote subscription is active for this group
    quotesStore.subscribeToQuotes();
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: document.body.scrollHeight,
    });
  } else {
    await router.push({ name: "Group" });
  }
}

function getPreviousDate(index: number): Date | undefined {
  if (index === 0) return undefined;
  const previousComment = comments.value[index - 1];
  return previousComment?.created_at;
}

async function postNewComment(e: { comment: string }) {
  logEvent(firebaseAnalytics, "added_comment", { for: "quote" });
  await postCommentToFirestore(e.comment);
}
</script>
